import { useState, useEffect } from "react"
import { router } from "@inertiajs/react"
import { Mic, Upload, PenLine, FileUp, Link, Star, Clock, FileText, Folder, StarOff, Loader2, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"

type CaptureMode = "voice" | "audio" | "editor" | "file" | null

interface FavoriteEntry {
  id: number
  path: string
  item_type: "file" | "folder"
  name: string
  position: number
}

interface RecentFileEntry {
  name: string
  path: string
  type: string
  serverModified?: string
}

interface CaptureScreenProps {
  onSelectMode: (mode: CaptureMode) => void
  dropboxConnected: boolean
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

const secondaryButtons = [
  {
    mode: "voice" as const,
    label: "Voice",
    description: "Record & transcribe",
    icon: Mic,
  },
  {
    mode: "audio" as const,
    label: "Audio",
    description: "Upload audio file",
    icon: Upload,
  },
  {
    mode: "file" as const,
    label: "Files",
    description: "Upload any file",
    icon: FileUp,
  },
]

export function CaptureScreen({ onSelectMode, dropboxConnected }: CaptureScreenProps) {
  const [favorites, setFavorites] = useState<FavoriteEntry[]>([])
  const [recentFiles, setRecentFiles] = useState<RecentFileEntry[]>([])
  const [loadingFavorites, setLoadingFavorites] = useState(true)
  const [loadingRecent, setLoadingRecent] = useState(true)

  useEffect(() => {
    if (dropboxConnected) {
      loadFavorites()
      loadRecentFiles()
    }
  }, [dropboxConnected])

  async function loadFavorites() {
    try {
      const res = await fetch("/api/favorites", {
        headers: { "X-CSRF-Token": getCSRFToken() },
      })
      const data = await res.json()
      setFavorites(data.favorites || [])
    } catch {
      // ignore
    } finally {
      setLoadingFavorites(false)
    }
  }

  async function loadRecentFiles() {
    try {
      const res = await fetch("/api/recent_files", {
        headers: { "X-CSRF-Token": getCSRFToken() },
      })
      const data = await res.json()
      const files: RecentFileEntry[] = data.files || []
      // Sort by most recently updated and take top 3
      const sorted = files
        .sort((a, b) => (b.serverModified || "").localeCompare(a.serverModified || ""))
        .slice(0, 3)
      setRecentFiles(sorted)
    } catch {
      // ignore
    } finally {
      setLoadingRecent(false)
    }
  }

  async function handleRemoveFavorite(path: string) {
    try {
      await fetch("/api/favorites", {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
        body: JSON.stringify({ path }),
      })
      setFavorites((prev) => prev.filter((f) => f.path !== path))
    } catch {
      // ignore
    }
  }

  function handleFileClick(path: string) {
    const filePath = path.replace(/^\/+/, "")
    const encoded = filePath.split("/").map((s) => encodeURIComponent(s)).join("/")
    router.visit(`/files/${encoded}`)
  }

  if (!dropboxConnected) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-6" data-testid="capture-screen">
        <div className="max-w-sm rounded-lg border bg-card shadow-xs p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            <Link className="h-8 w-8 text-muted-foreground" />
          </div>
          <h2 className="mb-2 text-xl font-semibold">Connect Dropbox to get started</h2>
          <p className="mb-6 text-sm text-muted-foreground">
            Brain Down saves your captures directly to Dropbox. Connect your account to start recording, writing, and uploading.
          </p>
          <Button onClick={() => (window.location.href = "/settings")}>
            Go to Settings
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col p-6 lg:pt-12" data-testid="capture-screen">
      <div className="mx-auto w-full max-w-2xl lg:max-w-4xl space-y-8">
        {/* New note section */}
        <div className="space-y-3">
          {/* Primary: Write button */}
          <button
            onClick={() => onSelectMode("editor")}
            className="flex w-full items-center justify-center gap-3 rounded-xl border-2 border-primary/20 bg-primary/5 p-5 hover:bg-primary/10 hover:border-primary/40 transition-all group"
            data-testid="capture-editor"
          >
            <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-sm">
              <PenLine className="h-5 w-5" />
            </div>
            <span className="text-base font-semibold text-foreground group-hover:text-primary transition-colors">Write</span>
          </button>

          {/* Secondary buttons row */}
          <div className="grid grid-cols-3 gap-3">
            {secondaryButtons.map((btn) => (
              <button
                key={btn.mode}
                onClick={() => onSelectMode(btn.mode)}
                className="flex items-center justify-center gap-2 rounded-lg border bg-card px-3 py-2.5 hover:bg-accent/50 transition-colors"
                data-testid={`capture-${btn.mode}`}
              >
                <btn.icon className="h-4 w-4 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">{btn.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Recent files section */}
        {dropboxConnected && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-sm font-medium text-muted-foreground">Recent</h2>
              </div>
              <button
                onClick={() => router.visit("/recent-files")}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                View all
                <ChevronRight className="h-3 w-3" />
              </button>
            </div>

            {loadingRecent ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : recentFiles.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                <p>No recent files yet.</p>
              </div>
            ) : (
              <div className="rounded-lg border bg-card shadow-xs overflow-hidden divide-y">
                {recentFiles.map((entry) => (
                  <div
                    key={entry.path}
                    className="group flex items-center gap-3 px-4 py-3 hover:bg-accent/50 cursor-pointer"
                    onClick={() => handleFileClick(entry.path)}
                  >
                    <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-medium">{entry.name}</p>
                    </div>
                    {entry.serverModified && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatRelativeTime(entry.serverModified)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Favorites section */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Star className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-medium text-muted-foreground">Favorites</h2>
          </div>

          {loadingFavorites ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : favorites.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <p>No favorites yet.</p>
              <p className="mt-1 text-xs">Use the context menu on any file or folder to add it.</p>
            </div>
          ) : (
            <div className="rounded-lg border bg-card shadow-xs overflow-hidden divide-y">
              {favorites.map((entry) => (
                <div
                  key={entry.id}
                  className="group flex items-center gap-3 px-4 py-3 hover:bg-accent/50 cursor-pointer"
                  onClick={() => handleFileClick(entry.path)}
                >
                  {entry.item_type === "folder" ? (
                    <Folder className="h-5 w-5 text-amber-500 shrink-0" />
                  ) : (
                    <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium">{entry.name}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveFavorite(entry.path)
                    }}
                    className="rounded-md p-1 text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors opacity-0 group-hover:opacity-100"
                    title="Remove from Favorites"
                  >
                    <StarOff className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
